/**
 * リンクを送る（工程管理の4回目、#103、docs/39 §6.3.2、Figma L1）。PC・タブレットはダイアログ、スマホは下からのシート
 *   初めて（リンクがまだ無い）：使える期間（30／90／180日と「12/29 まで」）・「現場の住所を見せる」・「現場の担当と電話を見せる」→「リンクを作る」
 *   リンクがある：同じ設定（変えるとすぐ保存）・リンク（真ん中を「…」で縮める）と「コピー」・LINE／メール／SMS で送る（このアプリからは送らない）・
 *     最後に開いた日時・「リンクを止める」「新しいリンクにする」（確かめてから）・「閉じる」
 * 送る文は contract の PARTNER_LINK_MSG.shareBody 1か所
 */
import { PARTNER_LINK_LIMITS, mdHmJst, mdJst, shortLinkText, type PartnerLinkView } from "@genbaryoku/contract";
import { PARTNER_LINK_MSG } from "./msg.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { createPortal } from "react-dom";
import { api, messageOf } from "../../api.js";
import { CrIcon } from "../../components/Create.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { useEsc } from "./Dialogs.jsx";
import { useWide } from "./hooks.js";

const DAY_MS = 86_400_000;
const untilOf = (days: number) => mdJst(new Date(Date.now() + days * DAY_MS).toISOString());
/** 送る文（LINE・メール・SMS 共通） */
const bodyOf = (v: PartnerLinkView) => (v.link ? PARTNER_LINK_MSG.shareBody(v.company, v.link.url, mdJst(v.link.expiresAt)) : "");

export function PartnerLinkDialog({ partnerId, partnerName, onClose, toast }: { partnerId: string; partnerName: string; onClose: () => void; toast: (t: string) => void }) {
  const phone = !useWide(768);
  const qc = useQueryClient();
  const key = ["partner-link", partnerId];
  const q = useQuery({ queryKey: key, queryFn: () => api<PartnerLinkView>("GET", `/partners/${partnerId}/schedule-link`), staleTime: 0, retry: false });
  const v = q.data;
  const [days, setDays] = useState<number>(PARTNER_LINK_LIMITS.defaultDays);
  const [showAddress, setShowAddress] = useState(true);
  const [showContact, setShowContact] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [ask, setAsk] = useState<"revoke" | "renew" | null>(null);
  useEsc(onClose);
  const name = v?.partner.name ?? partnerName;
  const link = v?.link ?? null;
  // リンクがあるときは、そのリンクの期間を出す（作ったときの長さ。期間を変えたあとは残りの日数にいちばん近いもの）
  const curDays = link
    ? (PARTNER_LINK_LIMITS.days.find((d) => Math.abs(new Date(link.expiresAt).getTime() - new Date(link.createdAt).getTime() - d * DAY_MS) < DAY_MS) ??
      [...PARTNER_LINK_LIMITS.days].sort((a, b) => Math.abs(a * DAY_MS - (new Date(link.expiresAt).getTime() - Date.now())) - Math.abs(b * DAY_MS - (new Date(link.expiresAt).getTime() - Date.now())))[0]!)
    : days;
  const set = (next: PartnerLinkView) => {
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: ["schedule-overview"] });
  };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(undefined);
    try {
      await fn();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const create = () =>
    run(async () => {
      set(await api<PartnerLinkView>("POST", `/partners/${partnerId}/schedule-link`, { days, showAddress, showContact }));
      toast(PARTNER_LINK_MSG.created);
    });
  const patch = (body: { days?: number; showAddress?: boolean; showContact?: boolean }) =>
    run(async () => {
      set(await api<PartnerLinkView>("PATCH", `/partners/${partnerId}/schedule-link`, body));
    });
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast(PARTNER_LINK_MSG.copied);
    } catch {
      setErr(PARTNER_LINK_MSG.copyFailed);
    }
  };

  const body = (
    <>
      <p className="sch-linklead">{PARTNER_LINK_MSG.linkLead(name)}</p>
      <div className="sch-linkrow">
        <b className="lbl">{PARTNER_LINK_MSG.linkDays}</b>
        <div className="sch-seg days" role="radiogroup" aria-label={PARTNER_LINK_MSG.linkDays}>
          {PARTNER_LINK_LIMITS.days.map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={curDays === d}
              className={curDays === d ? "on" : ""}
              disabled={busy}
              onClick={() => (link ? void patch({ days: d }) : setDays(d))}
              data-testid={`pl-link-days-${d}`}
            >
              {PARTNER_LINK_MSG.daysOption(d)}
            </button>
          ))}
        </div>
        <span className="muted" data-testid="pl-link-until">
          {link ? (link.expired ? PARTNER_LINK_MSG.linkChipExpired : PARTNER_LINK_MSG.until(mdJst(link.expiresAt))) : PARTNER_LINK_MSG.until(untilOf(days))}
        </span>
      </div>
      <label className="ck">
        <input
          type="checkbox"
          checked={link ? link.showAddress : showAddress}
          disabled={busy}
          onChange={(e) => (link ? void patch({ showAddress: e.target.checked }) : setShowAddress(e.target.checked))}
          data-testid="pl-link-show-address"
        />
        {PARTNER_LINK_MSG.showAddress}
      </label>
      <label className="ck">
        <input
          type="checkbox"
          checked={link ? link.showContact : showContact}
          disabled={busy}
          onChange={(e) => (link ? void patch({ showContact: e.target.checked }) : setShowContact(e.target.checked))}
          data-testid="pl-link-show-contact"
        />
        {PARTNER_LINK_MSG.showContact}
      </label>
      {link && v ? (
        <>
          <div className="sch-linkurl">
            <span className="u num" title={link.url} data-testid="pl-link-url" data-url={link.url}>
              {shortLinkText(link.url)}
            </span>
            <button type="button" className="btn primary" onClick={() => void copy()} data-testid="pl-link-copy">
              {PARTNER_LINK_MSG.copy}
            </button>
          </div>
          <div className="sch-linksend">
            <a className="btn" href={`https://line.me/R/share?text=${encodeURIComponent(bodyOf(v))}`} target="_blank" rel="noopener noreferrer" data-testid="pl-link-line">
              {PARTNER_LINK_MSG.sendLine}
            </a>
            {v.mailTo ? (
              <a className="btn" href={`mailto:${v.mailTo}?subject=${encodeURIComponent(PARTNER_LINK_MSG.shareSubject(v.company))}&body=${encodeURIComponent(bodyOf(v))}`} data-testid="pl-link-mail">
                {PARTNER_LINK_MSG.sendMail}
              </a>
            ) : (
              <button type="button" className="btn" disabled title={PARTNER_LINK_MSG.noMail} data-testid="pl-link-mail">
                {PARTNER_LINK_MSG.sendMail}
              </button>
            )}
            {v.smsTo ? (
              <a className="btn" href={`sms:${v.smsTo.replace(/[^0-9+]/g, "")}?&body=${encodeURIComponent(bodyOf(v))}`} data-testid="pl-link-sms">
                {PARTNER_LINK_MSG.sendSms}
              </a>
            ) : (
              <button type="button" className="btn" disabled title={PARTNER_LINK_MSG.noSms} data-testid="pl-link-sms">
                {PARTNER_LINK_MSG.sendSms}
              </button>
            )}
          </div>
          <p className="hint" data-testid="pl-link-opened">
            {link.lastOpenedAt ? PARTNER_LINK_MSG.lastOpened(mdHmJst(link.lastOpenedAt), link.openCount) : PARTNER_LINK_MSG.neverOpened}
          </p>
        </>
      ) : null}
      <ErrorText>{err}</ErrorText>
    </>
  );

  const foot = ask ? (
    <div className="sch-linkask" role="alertdialog" aria-label={ask === "revoke" ? PARTNER_LINK_MSG.revokeTitle : PARTNER_LINK_MSG.renewTitle} data-testid="pl-link-ask">
      <p>{ask === "revoke" ? PARTNER_LINK_MSG.revokeBody(name) : PARTNER_LINK_MSG.renewBody}</p>
      <div className="b">
        <button type="button" className="btn" onClick={() => setAsk(null)}>
          {PARTNER_LINK_MSG.cancel}
        </button>
        <button
          type="button"
          className={"btn" + (ask === "revoke" ? " shu" : " primary")}
          disabled={busy}
          onClick={() =>
            void run(async () => {
              if (ask === "revoke") {
                await api("DELETE", `/partners/${partnerId}/schedule-link`);
                set(await api<PartnerLinkView>("GET", `/partners/${partnerId}/schedule-link`));
                toast(PARTNER_LINK_MSG.revoked);
              } else {
                set(await api<PartnerLinkView>("POST", `/partners/${partnerId}/schedule-link`, { days: curDays ?? PARTNER_LINK_LIMITS.defaultDays, showAddress: link?.showAddress ?? true, showContact: link?.showContact ?? true }));
                toast(PARTNER_LINK_MSG.renewed);
              }
              setAsk(null);
            })
          }
          data-testid="pl-link-ask-ok"
        >
          {ask === "revoke" ? PARTNER_LINK_MSG.revoke : PARTNER_LINK_MSG.renew}
        </button>
      </div>
    </div>
  ) : link ? (
    <>
      <button type="button" className="btn text sch-redtext" onClick={() => setAsk("revoke")} data-testid="pl-link-revoke">
        {PARTNER_LINK_MSG.revoke}
      </button>
      <button type="button" className="btn text" onClick={() => setAsk("renew")} data-testid="pl-link-renew">
        {PARTNER_LINK_MSG.renew}
      </button>
      <span className="sp" />
      <button type="button" className="btn" onClick={onClose} data-testid="pl-link-close">
        {PARTNER_LINK_MSG.close}
      </button>
    </>
  ) : (
    <>
      <span className="sp" />
      <button type="button" className="btn" onClick={onClose}>
        {PARTNER_LINK_MSG.cancel}
      </button>
      <button type="button" className="btn primary" disabled={busy || !v} onClick={() => void create()} data-testid="pl-link-create">
        <CrIcon k="link" />
        {PARTNER_LINK_MSG.create}
      </button>
    </>
  );

  const title = PARTNER_LINK_MSG.linkTitle(name);
  return createPortal(
    phone ? (
      <div className="dim sch-sheetdim" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="sch-sheet sch-linksheet" role="dialog" aria-modal="true" aria-label={title} data-testid="pl-link-dialog" data-notice-above="">
          <span className="grab" aria-hidden="true" />
          <div className="hd">
            <b>{title}</b>
          </div>
          {q.isPending ? <p className="hint">読み込み中…</p> : q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : body}
          <div className="sch-linkfoot">{foot}</div>
        </div>
      </div>
    ) : (
      <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="dlg sch-linkdlg" role="dialog" aria-modal="true" aria-label={title} data-testid="pl-link-dialog">
          <div className="dh sch-dh">
            {title}
            <span className="sp" />
            <button type="button" className="btn text sq" aria-label={PARTNER_LINK_MSG.close} onClick={onClose}>
              ×
            </button>
          </div>
          <div className="db">{q.isPending ? <p className="hint">読み込み中…</p> : q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : body}</div>
          <div className="df">{foot}</div>
        </div>
      </div>
    ),
    document.body,
  );
}
